import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, BookmarkPlus, Check, ChevronDown, Trash2 } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import Dialog from './Dialog';
import type { MenuItemDef } from './Menu';
import { announce } from './LiveAnnouncer';
import MenuButton from '../table/MenuButton';
import {
  MAX_VIEW_NAME, addView, matchView, readLastView, readViews, removeView, safeLocalStorage,
  writeLastView, writeViews, type ListView,
} from '../table/savedViewsModel';

/**
 * One view switcher per list: "תצוגה: ‹name› ▾" (owner decision D1, PRD §4.11,
 * T-37). It lists the list's system views and the user's own views, saves the
 * current filters as a view (name + query, localStorage per user and list;
 * server storage is NL-04) and remembers the last view picked, which the list
 * re-applies when it opens with a bare URL (`getRememberedView`).
 *
 * The page owns the URL: `currentQuery` is the canonical query of the list's
 * view keys (`useListParams().query(VIEW_KEYS)`), `onApply(query)` applies a view.
 */
export interface SystemView {
  id: string;
  name: string;
  query: string;
}

interface SavedViewsProps {
  listId: string;
  systemViews: SystemView[];
  currentQuery: string;
  onApply: (query: string) => void;
  /** Contextual items after the views (e.g. "העדפות 'שלי'"). */
  extraItems?: MenuItemDef[];
  className?: string;
}

/** The last view the user picked on this list (null: none or storage unavailable). */
export function getRememberedView(listId: string, userId: string | undefined | null): string | null {
  if (!userId) return null;
  return readLastView(safeLocalStorage(), listId, userId);
}

export default function SavedViews({ listId, systemViews, currentQuery, onApply, extraItems = [], className = '' }: SavedViewsProps) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.id) ?? 'anon';
  const store = safeLocalStorage();
  const [saved, setSaved] = useState<ListView[]>(() => readViews(store, listId, userId));
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const current = useMemo(
    () => matchView(systemViews, currentQuery) ?? matchView(saved, currentQuery),
    [systemViews, saved, currentQuery],
  );
  const currentIsSaved = !!current && saved.some((v) => v.id === current.id);
  const currentName = current?.name ?? t('list.views.custom');

  const apply = (view: ListView) => {
    writeLastView(store, listId, userId, view.query);
    onApply(view.query);
  };

  const persist = (next: ListView[]) => {
    setSaved(next);
    writeViews(store, listId, userId, next);
  };

  const checkMark = <Check size={16} strokeWidth={1.75} className="text-accent-text" aria-hidden="true" />;
  const viewItem = (view: ListView, isSaved: boolean): MenuItemDef => ({
    key: `${isSaved ? 'u' : 's'}:${view.id}`,
    label: view.name,
    textValue: view.name,
    icon: isSaved ? <Bookmark size={16} strokeWidth={1.75} /> : undefined,
    trailing: current?.id === view.id ? checkMark : undefined,
    ariaLabel: current?.id === view.id ? t('list.views.currentItem', { name: view.name }) : undefined,
    onSelect: () => apply(view),
  });

  const items: MenuItemDef[] = [
    ...systemViews.map((v) => viewItem(v, false)),
    ...saved.map((v, i) => ({ ...viewItem(v, true), separatorBefore: i === 0 })),
  ];
  if (!current) {
    items.push({
      key: 'save',
      label: t('list.views.save'),
      icon: <BookmarkPlus size={16} strokeWidth={1.75} />,
      separatorBefore: true,
      onSelect: () => { setName(''); setSaving(true); },
    });
  }
  if (currentIsSaved && current) {
    items.push({
      key: 'delete',
      label: t('list.views.delete', { name: current.name }),
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      danger: true,
      separatorBefore: true,
      onSelect: () => {
        persist(removeView(saved, current.id));
        announce(t('list.views.deleted', { name: current.name }));
      },
    });
  }
  extraItems.forEach((item, i) => items.push({ ...item, separatorBefore: item.separatorBefore ?? i === 0 }));

  const submit = () => {
    const clean = name.trim();
    if (!clean) { inputRef.current?.focus(); return; }
    const next = addView(saved, clean, currentQuery, () => `v${Date.now().toString(36)}`);
    persist(next);
    writeLastView(store, listId, userId, currentQuery);
    setSaving(false);
    announce(t('list.views.saved', { name: clean }));
  };

  return (
    <>
      <MenuButton
        items={items}
        label={t('list.views.trigger', { name: currentName })}
        sheetTitle={t('list.views.sheetTitle')}
        align="start"
        popoverClassName="w-64 p-1.5"
        triggerClassName={`btn btn-secondary max-w-full justify-start gap-1.5 ${className}`}
      >
        <span className="text-text-2">{t('list.views.prefix')}</span>
        <span className="min-w-0 truncate font-medium text-text">{currentName}</span>
        <ChevronDown size={16} strokeWidth={1.75} className="shrink-0 text-text-2" aria-hidden="true" />
      </MenuButton>

      <Dialog
        open={saving}
        onClose={() => setSaving(false)}
        title={t('list.views.saveTitle')}
        size="sm"
        initialFocus={inputRef}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setSaving(false)}>{t('list.views.cancel')}</button>
            <button type="submit" form={`${listId}-save-view`} className="btn btn-primary">{t('list.views.saveConfirm')}</button>
          </>
        )}
      >
        <form
          id={`${listId}-save-view`}
          onSubmit={(e) => { e.preventDefault(); submit(); }}
        >
          <label className="label" htmlFor={`${listId}-view-name`}>{t('list.views.nameLabel')}</label>
          <input
            ref={inputRef}
            id={`${listId}-view-name`}
            className="input"
            value={name}
            maxLength={MAX_VIEW_NAME}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('list.views.namePlaceholder')}
            autoComplete="off"
            required
          />
          <p className="mt-2 text-xs text-text-2">{t('list.views.localHint')}</p>
        </form>
      </Dialog>
    </>
  );
}
